<template>
  <navigation v-if="authState == 'signed-in'" />
  <div v-if="authState == 'signed-in'" class="kk__designs" @mousemove="mouseMove" @mouseup="mouseUp">
    <panel @resizing-started="startResizing = true" />
    <permission-manager v-if="showListDialog" />

    <process-designer :design-id="designId" />
    <go-to v-if="showGoTo" />
    <CreateProcessFromSAP v-if="showCreateSap" />

    <find v-if="showFind" ref="input" @select-match="selectCell" @close="showFind = false" />
    <add-series-dialog v-if="showSeries" />

    <confirm-delete v-if="showDialog" />

    <error-boundary v-if="error" />

    <formula-box v-if="showFormulaBox" />

    <custom-sort v-if="showCustomSort" />

    <!-- we have to put context menu outside of process designer body. because it has overflow auto
      which causes our context menu to get clipped and not be shown fully. 
      Now that context menu is here we calculate Y (top) and X (left) position from viewport to align
      context menu properly next to activity 
    -->
    <activity-context-menu v-if="showActivityContextMenu" />
    <div
      v-if="activeSubNavigation != SubNavigations.Settings"
      class="kk__bluezoneTrigger"
      :style="blueZoneStyle"
      @click="showBlueZone"
    >
      <i class="icon-chevron-left"></i>
    </div>

    <div v-if="activeSubNavigation != SubNavigations.Settings && isBlueZoneActive" class="kk__designs--bluezone">
      <blue-zone />
    </div>
  </div>
</template>

<script>
import { cellIndexToLabel, isRangeWithinRange } from "../utils/util";
import { getCellId, rangeLabelToIndex, sortRange } from "../components/process-design/cell/Utils";
import { SubNavigations } from "../utils/constants";
import hotkeys from "hotkeys-js";
import Find from "../components/process-design/find/Find.vue";
import ProcessDesigner from "../components/process-design/ProcessDesigner";
import { Preferences } from "../utils/constants";
import ActivityContextMenu from "../components/process-design/body/sidebar/activity-context-menu/ActivityContextMenu";
import GoTo from "../components/process-design/goto/GoTo.vue";
import Panel from "../components/redZone/RedZone.vue";
import DesignerMixin from "../components/process-design/DesignerMixin";
import ConfirmDelete from "../components/redZone/content/ConfirmDelete.vue";
import Navigation from "../components/navbar/Navigation";
import AddSeriesDialog from "../components/process-design/access-toolbar/AddSeriesDialog.vue";
import ErrorBoundary from "../components/errorHandling/ErrorBoundary.vue";
import BlueZone from "../components/blueZone/BlueZone.vue";
import FormulaBox from "../components/redZone/content/FormulaBox.vue";
import CreateProcessFromSAP from "../components/sap/CreateProcessFromSAP.vue";
import PermissionManager from "../components/process-design/permissions/PermissionManager.vue";
import CustomSort from "../components/commons/CustomSort.vue";

export default {
  components: {
    ErrorBoundary,
    ProcessDesigner,
    ActivityContextMenu,
    Panel,
    Navigation,
    GoTo,
    ConfirmDelete,
    AddSeriesDialog,
    Find,
    BlueZone,
    FormulaBox,
    CustomSort,
    CreateProcessFromSAP,
    PermissionManager,
  },
  mixins: [DesignerMixin],
  data() {
    return {
      startResizing: false,
      showFind: false,
      SubNavigations: SubNavigations,
    };
  },
  computed: {
    showListDialog() {
      return this.$store.state.pdesign.selectedItemId;
    },
    showCustomSort() {
      return this.$store.state.pdesign.showCustomSort;
    },
    showFormulaBox() {
      return this.$store.state.pdesign.showFormulaBox;
    },
    blueZoneStyle() {
      return {
        background: this.isBlueZoneActive ? "#083277" : "#5292f780",
      };
    },
    isBlueZoneActive() {
      return this.$store.state.pdesign.blueZone;
    },
    activeSubNavigation() {
      return this.$store.state.preferences.allPreferences?.activeSubNavigation;
    },
    error() {
      return this.$store.state.pdesign.error?.show;
    },
    showGoTo() {
      return this.$store.state.pdesign.isGotoBoxVisible;
    },
    showSmartSearch() {
      return this.$store.state.pdesign.isSmartSearchBoxVisible;
    },
    showSeries() {
      return this.$store.state.views.showSeriesDialog;
    },
    showDialog() {
      return this.$store.state.panel.deleteDialog.show;
    },
    showCreateSap() {
      return this.$store.state.cells.showCreateSapDialog;
    },
    mybusiness() {
      return this.$store.state.subscription.action.status.loading == "loaded";
    },
    authState() {
      return this.$store.state.authState;
    },
    authUser() {
      return this.$store.state.authUser;
    },
    designId() {
      return this.$route.params.id;
    },
    contentFocused() {
      return this.$store.state.pdesign.contentFocused;
    },
    left() {
      return this.$store.state.preferences.allPreferences?.panelWidth + 4 ?? 20.5;
    },
    showActivityContextMenu() {
      if (!this.mainRange?.range) return;

      const range = this.$store.getters["cells/activeRange"]().range;
      const isConflict = this.$store.getters["table/tableHeaderConflict"](range);
      const isTotalConflict = this.$store.getters["table/tableTotalConflict"](range);
      const boundaries = [0, 0, this.columns.length - 1, this.rows.length - 1];

      // don't show context menu if there any table header in range
      if (
        ((isConflict || isTotalConflict) &&
          !(this.mainRange.range[1] === boundaries[1] && this.mainRange.range[3] === boundaries[3])) ||
        !isRangeWithinRange(range, boundaries)
      ) {
        return false;
      }

      const columnIds = this.$store.getters["cells/columnsInRange"](range);
      const rowIds = this.$store.getters["cells/rowsInRange"](range);

      const columnsList = columnIds.map((columnId) => this.columnMap[columnId]);
      const rowList = rowIds.map((rowId) => this.rowMap[rowId]);

      const sameRowTypes = rowList.every((obj) => obj.type === rowList[0].type);
      const sameColumnTypes = columnsList.every((obj) => obj.type === columnsList[0].type);

      // Check for rows or columns selection
      const isSingleRowSelected =
        this.mainRange.range[0] === boundaries[0] &&
        this.mainRange.range[2] === boundaries[2] &&
        this.mainRange.range[1] === this.mainRange.range[3];

      const isSingleColumnSelected =
        this.mainRange.range[1] === boundaries[1] &&
        this.mainRange.range[3] === boundaries[3] &&
        this.mainRange.range[0] === this.mainRange.range[2];

      const isMultipleRowsSelected =
        this.mainRange.range[1] !== this.mainRange.range[3] && // Two or more rows selected
        this.mainRange.range[0] === boundaries[0] &&
        this.mainRange.range[2] === boundaries[2];

      const isMultipleColumnsSelected =
        this.mainRange.range[0] !== this.mainRange.range[2] && // Two or more columns selected
        this.mainRange.range[1] === boundaries[1] &&
        this.mainRange.range[3] === boundaries[3];

      // Return true if single or multiple rows/columns are selected
      return (
        isSingleRowSelected ||
        isSingleColumnSelected ||
        (isMultipleRowsSelected && sameRowTypes) ||
        (isMultipleColumnsSelected && sameColumnTypes)
      );
    },
  },
  beforeMount() {
    if (this.designId) {
      this.$store.dispatch("cells/load", this.designId);
    } else {
      this.$store.dispatch("cells/resetState");
    }
    this.$store.dispatch("preferences/load");
  },
  mounted() {
    hotkeys("ctrl+f,escape", (event, handler) => {
      event.preventDefault();
      switch (handler.key) {
        case "ctrl+f":
          if (this.showFind) {
            this.$nextTick(() => {
              if (this.$refs.input && this.$refs.input.updateFind) {
                this.$refs.input.updateFind();
              }
            });
          }
          if (this.activeSubNavigation !== SubNavigations.Settings) {
            this.showFind = true;
            this.$nextTick(() => {
              this.$refs.input.$el.querySelector(".kk__find--input").focus();
            });
          }
          break;
        case "escape":
          this.showFind = false;
          this.$refs.input?.close();
          this.$store.dispatch("pdesign/toggleFormulaBox", false);
          break;
      }
    });
  },
  beforeUnmount() {
    hotkeys.unbind("ctrl+f");
  },
  methods: {
    showBlueZone(e) {
      e.preventDefault();
      this.$store.dispatch("pdesign/toggleBlueZone");
    },
    selectCell(cellIndex) {
      const cellLabel = cellIndexToLabel(+cellIndex.columnIndex, +cellIndex.rowIndex);
      const range = sortRange(rangeLabelToIndex(cellLabel));
      if (range[2] >= this.columns.length || range[3] >= this.rows.length) return;
      const activeCellId = getCellId(this.rows[range[1]].id, this.columns[range[0]].id);
      this.$store.dispatch("cells/setFormulaRange", { title: cellLabel, activeCellId });
      this.$store.dispatch("cells/setShouldScroll");
    },
    mouseMove(e) {
      if (this.startResizing) {
        // don't resize if it is too small or too large.
        if (e.clientX - 16 > window.innerWidth / 2 || e.clientX < 360) return;

        this.$store.dispatch("preferences/updateWithoutPersist", {
          key: Preferences.PanelWidth,
          value: e.clientX - 16,
        });
      }
    },
    mouseUp() {
      if (this.startResizing) this.$store.dispatch("preferences/persist");
      this.startResizing = false;
    },
  },
};
</script>

<style lang="less" scoped>
@import "../assets/styles/variables";

.kk {
  &__designs {
    display: flex;
    gap: 1rem;
    position: relative;
    flex-grow: 1;

    &--bluezone {
      position: absolute;
      z-index: 100;
      top: 0;
      bottom: 0px;
      right: 28px;
      padding: 1rem 0 1rem 0rem;
      background: @kk-color-background;
    }
  }

  &__bluezoneTrigger {
    align-self: center;
    display: flex;
    justify-content: center;
    align-items: center;
    width: 1.1rem;
    height: 1.5rem;
    color: @kk-color-white;
    cursor: pointer;
    box-shadow: -3px 0px 2px 0px rgba(0, 0, 0, 0.15) inset;
    border-radius: 4px 0px 0px 4px;
  }
}
</style>
